import type { NodeTypes } from '@xyflow/react';
import type { OutputsResolver, WidgetDefinition, WidgetCategory, WidgetTier } from './types';

type WidgetMetadata = {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: WidgetCategory;
  tier: WidgetTier;
};

function toMetadata(widget: WidgetDefinition): WidgetMetadata {
  return {
    id: widget.id,
    name: widget.name,
    description: widget.description,
    icon: widget.icon,
    category: widget.category,
    tier: widget.tier,
  };
}

function replaceGrouped<TKey>(groups: Map<TKey, WidgetDefinition[]>, key: TKey, widget: WidgetDefinition) {
  groups.set(key, [...(groups.get(key) ?? []).filter((w) => w.id !== widget.id), widget]);
}

export function createWidgetRegistry() {
  const widgets = new Map<string, WidgetDefinition>();
  const byCategory = new Map<WidgetCategory, WidgetDefinition[]>();
  const byTier = new Map<WidgetTier, WidgetDefinition[]>();

  function getByCategory(category: WidgetCategory) {
    return byCategory.get(category) ?? [];
  }

  return {
    register<TConfig>(widget: WidgetDefinition<TConfig>) {
      const existing = widgets.get(widget.id);

      if (existing && widget.version <= existing.version) {
        console.warn(`Widget ${widget.id} v${widget.version} skipped (v${existing.version} already registered)`);
        return;
      }

      const definition = widget as WidgetDefinition;

      widgets.set(definition.id, definition);
      replaceGrouped(byCategory, definition.category, definition);
      replaceGrouped(byTier, definition.tier, definition);
    },

    get(id: string) {
      return widgets.get(id);
    },

    getAll() {
      return [...widgets.values()];
    },

    getByCategory,

    getByTier(tier: WidgetTier) {
      return byTier.get(tier) ?? [];
    },

    getCategories() {
      return [...byCategory.keys()];
    },

    getNodeTypes(): NodeTypes {
      const nodeTypes: Record<string, unknown> = {};

      for (const widget of widgets.values()) {
        nodeTypes[widget.id] = widget.ui.component;
      }

      return nodeTypes as NodeTypes;
    },

    getMetadata() {
      return [...widgets.values()].map(toMetadata);
    },

    getMetadataByCategory() {
      return [...byCategory.keys()].map((category) => ({ category, widgets: getByCategory(category).map(toMetadata) }));
    },

    has(id: string) {
      return widgets.has(id);
    },

    count() {
      return widgets.size;
    },
  };
}

export const widgetRegistry = createWidgetRegistry();

export function registerWidget<TConfig>(widget: WidgetDefinition<TConfig>) {
  widgetRegistry.register(widget);
}

export const widgetOutputs: OutputsResolver = (nodeType, config) => {
  const widget = widgetRegistry.get(nodeType);
  if (!widget) return undefined;
  return widget.getDynamicOutputs?.(config) ?? widget.handles.outputs;
};
